import {
  AvailabilityFilter,
  EVERY_ADVISER_AVAILABILITY_FILTER,
  SchedulerOwningEntityType,
} from '@/components/integration/nylas/scheduler/types'
import { API_PATH_INTERNAL } from '@/lib/constants'
import apiService from '@/lib/services/apiService'
import { AvailabilityItem } from '@/types/responses/availability'

class AvailabilityService {
  async getAvailability(
    owningEntityType: SchedulerOwningEntityType,
    owningEntityId: number | string,
    start: Date,
    end: Date,
    /**
     * For an in-person meeting: the format, so the API applies the meeting
     * type's in-person booking rules, and optionally an office, to only
     * include the advisers based there.
     */
    availabilityFilter: AvailabilityFilter = EVERY_ADVISER_AVAILABILITY_FILTER,
  ): Promise<AvailabilityItem> {
    const startTimestamp = Math.floor(start.getTime() / 1000)
    const endTimestamp = Math.floor(end.getTime() / 1000)
    const locationQuery =
      availabilityFilter.locationId === null
        ? ''
        : `&locationId=${availabilityFilter.locationId}`
    const inPersonFormatQuery =
      availabilityFilter.inPersonFormat === null
        ? ''
        : `&inPersonFormat=${availabilityFilter.inPersonFormat}`

    const endpoint = `${API_PATH_INTERNAL}/${owningEntityType}/${owningEntityId}/availability?start=${startTimestamp}&end=${endTimestamp}${locationQuery}${inPersonFormatQuery}`

    const response = await apiService.get<AvailabilityItem>(endpoint)

    return response.data
  }

  /**
   * Availability for moving the referral's existing meeting. The current
   * meeting's slot is treated as free so it can be kept or shifted.
   */
  async getRescheduleAvailability(
    referralId: number,
    start: Date,
    end: Date,
    /** The format and office the meeting is being moved to, as for getAvailability. */
    availabilityFilter: AvailabilityFilter = EVERY_ADVISER_AVAILABILITY_FILTER,
  ): Promise<AvailabilityItem> {
    const response = await apiService.get<AvailabilityItem>(
      `${API_PATH_INTERNAL}/referrals/${referralId}/reschedule-availability`,
      {
        params: this.buildRescheduleAvailabilityParams(
          start,
          end,
          availabilityFilter,
        ),
      },
    )

    return response.data
  }

  /**
   * Guest equivalent of getRescheduleAvailability, authorised by the
   * referral's callback code.
   */
  async getGuestRescheduleAvailability(
    referralId: number,
    callbackCode: string,
    start: Date,
    end: Date,
    availabilityFilter: AvailabilityFilter = EVERY_ADVISER_AVAILABILITY_FILTER,
  ): Promise<AvailabilityItem> {
    const response = await apiService.get<AvailabilityItem>(
      `${API_PATH_INTERNAL}/referrals/${referralId}/guest/reschedule-availability`,
      {
        params: {
          callbackCode,
          ...this.buildRescheduleAvailabilityParams(
            start,
            end,
            availabilityFilter,
          ),
        },
      },
    )

    return response.data
  }

  /**
   * The office and in-person format are left out for a video or phone
   * meeting, so the API shows every adviser's times.
   */
  private buildRescheduleAvailabilityParams(
    start: Date,
    end: Date,
    availabilityFilter: AvailabilityFilter,
  ) {
    return {
      start: Math.floor(start.getTime() / 1000),
      end: Math.floor(end.getTime() / 1000),
      locationId: availabilityFilter.locationId ?? undefined,
      inPersonFormat: availabilityFilter.inPersonFormat ?? undefined,
    }
  }

  /**
   * The first 7 days of availability, from midnight this morning (local
   * time) up to midnight 7 days later. This is the range the scheduler
   * loads and displays initially.
   */
  getInitialAvailabilityDateRange() {
    const midnightThisMorning = new Date()
    midnightThisMorning.setHours(0, 0, 0, 0)

    const midnightAtEndOfSevenDays = new Date(midnightThisMorning)
    midnightAtEndOfSevenDays.setDate(midnightThisMorning.getDate() + 7)

    return {
      start: midnightThisMorning,
      end: midnightAtEndOfSevenDays,
    }
  }

  /**
   * The 7 days immediately after the initial range, from midnight 7 days
   * from now up to midnight 14 days from now (local time). Used to prefetch
   * the following week's availability so it's ready when the user needs it.
   */
  getExtendedAvailabilityDateRange() {
    const midnightAtStartOfNextSevenDays = new Date()
    midnightAtStartOfNextSevenDays.setHours(0, 0, 0, 0)
    midnightAtStartOfNextSevenDays.setDate(
      midnightAtStartOfNextSevenDays.getDate() + 7,
    )

    const midnightAtEndOfFourteenDays = new Date(midnightAtStartOfNextSevenDays)
    midnightAtEndOfFourteenDays.setDate(
      midnightAtStartOfNextSevenDays.getDate() + 7,
    )

    return {
      start: midnightAtStartOfNextSevenDays,
      end: midnightAtEndOfFourteenDays,
    }
  }

  /**
   * The full calendar month that `month` falls in, from midnight on the 1st
   * (local time) up to midnight on the 1st of the following month. Used to
   * prefetch a whole month of availability for the calendar day picker.
   */
  getMonthDateRange(month: Date) {
    const monthStart = new Date(
      month.getFullYear(),
      month.getMonth(),
      1,
      0,
      0,
      0,
      0,
    )

    const monthEnd = new Date(
      month.getFullYear(),
      month.getMonth() + 1,
      1,
      0,
      0,
      0,
      0,
    )

    return {
      start: monthStart,
      end: monthEnd,
    }
  }
}

const availabilityService = new AvailabilityService()

export default availabilityService
